import { useState, useEffect, useCallback, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Switch } from '../../components/ui/switch';
import { Upload, AlertCircle, CheckCircle, ChevronRight, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { compensationAPI, carrierAPI } from '../../lib/api';
import { MappingHealthPanel } from './MappingHealthPanel';

export function ImportTab({ onImported }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [statementMonth, setStatementMonth] = useState('');
  const [carrierId, setCarrierId] = useState('');
  const [preview, setPreview] = useState(null);
  const [columnMap, setColumnMap] = useState({});
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [constants, setConstants] = useState({});
  const [saveTemplate, setSaveTemplate] = useState({ enabled: false, name: '', version: 'v1' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [statements, setStatements] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [deletingId, setDeletingId] = useState(null);

  const loadStatements = useCallback(async () => {
    try { setStatements((await compensationAPI.listStatements()).data.statements || []); }
    catch (e) { console.warn('Failed to load past imports:', e); }
  }, []);
  useEffect(() => { loadStatements(); }, [loadStatements]);

  // Load the user's carrier list for the dropdown (was a free-text input before).
  useEffect(() => {
    (async () => {
      try {
        const r = await carrierAPI.list();
        // The carriers endpoint returns the raw list (no envelope).
        const list = Array.isArray(r.data) ? r.data : (r.data?.carriers || []);
        // Sort by name for predictable picker UX.
        list.sort((a, b) => (a.carrier_name || '').localeCompare(b.carrier_name || ''));
        setCarriers(list);
      } catch (e) { console.warn('Carrier list unavailable, falling back to manual entry:', e); }
    })();
  }, []);

  const handleDeleteStatement = async (s) => {
    const recCount = (s.imported ?? 0) + (s.needs_review ?? 0);
    const msg =
      `Delete the statement "${s.original_filename}"?\n\n` +
      `This will remove the statement record` +
      (recCount > 0 ? ` AND ${recCount} compensation record(s) it created.` : '.') +
      `\n\nPaid records are PRESERVED (immutable ledger). This cannot be undone.`;
    if (!window.confirm(msg)) return;
    setDeletingId(s.statement_id);
    try {
      const r = await compensationAPI.deleteStatement(s.statement_id);
      const d = r.data || {};
      toast.success(
        `Deleted import — removed ${d.deleted_records || 0} record(s)` +
        (d.protected_paid_records ? `, kept ${d.protected_paid_records} Paid` : '')
      );
      await loadStatements();
      if (onImported) onImported();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  const startOver = () => {
    setStep(1); setFile(null); setPreview(null); setColumnMap({}); setSelectedTemplate(null); setConstants({}); setResult(null);
    setSaveTemplate({ enabled: false, name: '', version: 'v1' });
  };

  const onPreview = async () => {
    if (!file) { toast.error('Choose a file'); return; }
    if (!statementMonth) { toast.error('Statement month is required'); return; }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('statement_month', statementMonth);
    if (carrierId) fd.append('carrier_id', carrierId);
    setLoading(true);
    try {
      const r = await compensationAPI.previewStatement(fd);
      setPreview(r.data);
      const top = (r.data.suggested_templates || [])[0];
      if (top && top.confidence_score >= 50) {
        setColumnMap(top.column_map || {});
        setSelectedTemplate(top);
        setConstants(top.constants || {});
      } else {
        setColumnMap({});
      }
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Preview failed');
    } finally { setLoading(false); }
  };

  const applyTemplate = (tmpl) => {
    setSelectedTemplate(tmpl);
    setColumnMap(tmpl.column_map || {});
    setConstants(tmpl.constants || {});
  };

  const setMapping = (rawCol, sysField) => {
    setColumnMap((prev) => {
      const next = { ...prev };
      if (!sysField || sysField === '__none__') {
        delete next[rawCol];
      } else {
        next[rawCol] = sysField;
      }
      return next;
    });
  };

  const mappedFields = useMemo(() => new Set(Object.values(columnMap || {})), [columnMap]);
  const missingRequired = useMemo(() => {
    const required = ['policy_holder', 'actual_carrier_paid'];
    return required.filter((f) => !mappedFields.has(f) && !(constants && constants[f]));
  }, [mappedFields, constants]);

  const onCommit = async () => {
    if (missingRequired.length > 0) {
      toast.error(`Map required fields: ${missingRequired.join(', ')}`); return;
    }
    setLoading(true);
    try {
      const payload = {
        statement_id: preview.statement_id,
        statement_month: statementMonth,
        carrier_id: carrierId || null,
        column_map: columnMap,
        constants,
      };
      if (saveTemplate.enabled) {
        if (!saveTemplate.name.trim()) { setLoading(false); toast.error('Template name is required to save'); return; }
        payload.save_as_template = {
          name: saveTemplate.name,
          carrier_id: carrierId || null,
          version: saveTemplate.version || 'v1',
          column_map: columnMap,
        };
      }
      if (selectedTemplate?.template_id) {
        payload.template_id = selectedTemplate.template_id;
      }
      const r = await compensationAPI.commitStatement(payload);
      setResult(r.data);
      setStep(3);
      loadStatements();
      onImported?.();
      toast.success(`Imported ${r.data.summary.imported}, ${r.data.summary.needs_review} need review`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Commit failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm">
        {['Upload', 'Map columns', 'Import results'].map((label, i) => (
          <div key={label} className="flex items-center gap-2">
            <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold ${step >= i + 1 ? 'bg-sky-600 text-white' : 'bg-zinc-200 text-zinc-500'}`}>{i + 1}</div>
            <span className={step >= i + 1 ? 'font-medium' : 'text-zinc-500'}>{label}</span>
            {i < 2 && <ChevronRight className="h-4 w-4 text-zinc-400" />}
          </div>
        ))}
        {step > 1 && <Button variant="ghost" size="sm" className="ml-auto" onClick={startOver} data-testid="import-start-over">Start over</Button>}
      </div>

      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Upload Carrier Statement</CardTitle>
            <CardDescription>CSV or Excel. System will preview columns and suggest a mapping template.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 md:grid-cols-3">
              <div>
                <Label>Statement Month *</Label>
                <Input data-testid="comp-import-month" type="month" value={statementMonth} onChange={(e) => setStatementMonth(e.target.value)} />
              </div>
              <div>
                <Label>Carrier *</Label>
                <Select value={carrierId || '__none__'} onValueChange={(v) => setCarrierId(v === '__none__' ? '' : v)}>
                  <SelectTrigger data-testid="comp-import-carrier-select">
                    <SelectValue placeholder="Select a carrier from your list" />
                  </SelectTrigger>
                  <SelectContent className="max-h-80">
                    <SelectItem value="__none__">— Auto-detect from file —</SelectItem>
                    {carriers.map((c) => (
                      <SelectItem
                        key={c.carrier_id}
                        value={c.carrier_id}
                        data-testid={`comp-import-carrier-opt-${c.carrier_id}`}
                      >
                        {c.carrier_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-[11px] text-zinc-500 mt-1">
                  Pick the same carrier on every upload to avoid duplicate IDs like "WellCare" vs "Wellcare".
                </p>
              </div>
              <div>
                <Label>File (CSV / XLSX) *</Label>
                <Input data-testid="comp-import-file" type="file" accept=".csv,.xlsx,.xls" onChange={(e) => setFile(e.target.files?.[0])} />
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={onPreview} disabled={loading || !file || !statementMonth} data-testid="comp-import-preview-btn">
                <Upload className="h-4 w-4 mr-1" />{loading ? 'Reading…' : 'Preview & Map'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 2 && preview && (
        <div className="space-y-4">
          {/* File preview — first 5 rows from the uploaded file */}
          {Array.isArray(preview.sample_rows) && preview.sample_rows.length > 0 && (
            <Card data-testid="comp-import-file-preview">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">File Preview</CardTitle>
                <CardDescription>
                  First {Math.min(5, preview.sample_rows.length)} rows from <code className="text-xs">{file?.name}</code>.
                  Verify the data looks right before mapping columns.
                </CardDescription>
              </CardHeader>
              <CardContent className="overflow-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {preview.columns.map((c) => (
                        <TableHead key={c} className="text-xs uppercase tracking-wide whitespace-nowrap">{c}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.sample_rows.slice(0, 5).map((row, i) => (
                      <TableRow key={i}>
                        {preview.columns.map((c) => (
                          <TableCell key={c} className="text-xs whitespace-nowrap max-w-[200px] truncate" title={String(row[c] ?? '')}>
                            {row[c] === null || row[c] === undefined || row[c] === '' ? <span className="text-zinc-300">—</span> : String(row[c])}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {preview.suggested_templates?.length > 0 && (
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Suggested Templates</CardTitle></CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {preview.suggested_templates.slice(0, 5).map((t) => (
                  <button key={t.template_id} type="button"
                          onClick={() => applyTemplate(t)}
                          data-testid={`import-template-${t.template_id}`}
                          className={`text-left rounded border px-3 py-2 hover:bg-sky-50 transition ${selectedTemplate?.template_id === t.template_id ? 'border-sky-500 bg-sky-50' : 'border-zinc-200'}`}>
                    <div className="text-sm font-medium">{t.name}</div>
                    <div className="text-xs text-zinc-500 mt-1">{t.is_starter ? 'Starter' : 'Saved'} · v{t.version || '1'}</div>
                    <Badge className={`mt-1 ${t.confidence_score >= 80 ? 'bg-emerald-100 text-emerald-800' : t.confidence_score >= 50 ? 'bg-amber-100 text-amber-800' : 'bg-zinc-100 text-zinc-700'}`}>
                      {t.confidence_score}% confidence
                    </Badge>
                  </button>
                ))}
              </CardContent>
            </Card>
          )}
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Map Columns to System Fields</CardTitle><CardDescription>Required: policy_holder, actual_carrier_paid. Unmapped columns are ignored.</CardDescription></CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow><TableHead>Uploaded Column</TableHead><TableHead>System Field</TableHead><TableHead>Sample value</TableHead></TableRow></TableHeader>
                <TableBody>
                  {preview.columns.map((col) => (
                    <TableRow key={col} data-testid={`import-col-${col}`}>
                      <TableCell className="text-sm font-medium">{col}</TableCell>
                      <TableCell>
                        <Select value={columnMap[col] || '__none__'} onValueChange={(v) => setMapping(col, v)}>
                          <SelectTrigger className="w-60" data-testid={`import-map-select-${col}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— Ignore this column —</SelectItem>
                            {preview.system_fields.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-xs text-zinc-600">{String(preview.sample_rows?.[0]?.[col] ?? '—').slice(0, 80)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {missingRequired.length > 0 && (
                <div className="mt-3 text-sm text-amber-700 flex items-center gap-2"><AlertCircle className="h-4 w-4" />Missing required mappings: <b>{missingRequired.join(', ')}</b></div>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 flex flex-wrap items-center gap-3">
              <Switch checked={saveTemplate.enabled} onCheckedChange={(v) => setSaveTemplate((p) => ({ ...p, enabled: v }))} data-testid="import-save-template-toggle" />
              <Label>Save this mapping as a reusable template</Label>
              {saveTemplate.enabled && (
                <div className="flex items-center gap-2 ml-2">
                  <Input placeholder="Template name (e.g. Humana_v2)" className="w-60" value={saveTemplate.name}
                         onChange={(e) => setSaveTemplate((p) => ({ ...p, name: e.target.value }))}
                         data-testid="import-save-template-name" />
                  <Input placeholder="Version" className="w-24" value={saveTemplate.version}
                         onChange={(e) => setSaveTemplate((p) => ({ ...p, version: e.target.value }))} />
                </div>
              )}
              <div className="flex-1" />
              <Button onClick={onCommit} disabled={loading || missingRequired.length > 0} data-testid="comp-import-commit-btn">
                {loading ? 'Importing…' : 'Validate & Import'}
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {step === 3 && result && (
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center gap-2"><CheckCircle className="h-5 w-5 text-emerald-700" /><div className="font-medium text-emerald-900">Import complete</div></div>
            <div className="grid grid-cols-5 gap-3 text-sm">
              <div><div className="text-xs text-emerald-700">Total rows</div><div className="font-semibold">{result.summary.total}</div></div>
              <div><div className="text-xs text-emerald-700">Imported</div><div className="font-semibold">{result.summary.imported}</div></div>
              <div><div className="text-xs text-emerald-700">Needs review</div><div className="font-semibold">{result.summary.needs_review}</div></div>
              <div><div className="text-xs text-emerald-700">Adjustments</div><div className="font-semibold">{result.summary.adjustments}</div></div>
              <div><div className="text-xs text-emerald-700">Duplicates flagged</div><div className="font-semibold">{result.summary.duplicates_flagged}</div></div>
            </div>
            {result.summary.errors?.length > 0 && (
              <details className="text-xs text-emerald-900"><summary>{result.summary.errors.length} errors</summary><pre className="bg-white/60 p-2 rounded mt-2 overflow-auto max-h-60">{JSON.stringify(result.summary.errors, null, 2)}</pre></details>
            )}
            {result.saved_template && (
              <div className="text-xs text-emerald-700">Saved as template: <b>{result.saved_template.name}</b> ({result.saved_template.template_id})</div>
            )}
            <div className="flex gap-2">
              <Button variant="outline" onClick={startOver}>Import another</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <MappingHealthPanel />

      <Card>
        <CardHeader><CardTitle className="text-base">Past Imports</CardTitle></CardHeader>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Uploaded</TableHead>
              <TableHead>Month</TableHead>
              <TableHead>Carrier</TableHead>
              <TableHead>File</TableHead>
              <TableHead className="text-right">Rows</TableHead>
              <TableHead className="text-right">Imported</TableHead>
              <TableHead className="text-right">Review</TableHead>
              <TableHead className="text-right">Dups</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {statements.length === 0 && (
              <TableRow><TableCell colSpan={10} className="text-center text-zinc-500 py-6">No imports yet.</TableCell></TableRow>
            )}
            {statements.map((s) => (
              <TableRow key={s.statement_id} data-testid={`comp-statement-row-${s.statement_id}`}>
                <TableCell className="text-sm">{new Date(s.uploaded_at).toLocaleString()}</TableCell>
                <TableCell>{s.statement_month}</TableCell>
                <TableCell className="text-sm">{s.carrier_id || '—'}</TableCell>
                <TableCell className="text-sm truncate max-w-xs">{s.original_filename}</TableCell>
                <TableCell className="text-right">{s.total_rows ?? '—'}</TableCell>
                <TableCell className="text-right">{s.imported ?? 0}</TableCell>
                <TableCell className="text-right">{s.needs_review ?? 0}</TableCell>
                <TableCell className="text-right">{s.duplicates_flagged ?? 0}</TableCell>
                <TableCell><Badge className={s.status === 'Completed' ? 'bg-emerald-100 text-emerald-800' : s.status === 'Failed' ? 'bg-red-100 text-red-800' : 'bg-zinc-100 text-zinc-700'}>{s.status}</Badge></TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteStatement(s)}
                    disabled={deletingId === s.statement_id}
                    data-testid={`comp-statement-delete-${s.statement_id}`}
                    title="Delete this import"
                  >
                    {deletingId === s.statement_id
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Trash2 className="w-4 h-4 text-rose-600" />
                    }
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
